/**
 * Active Admin Direct Upload Enhancer
 * 
 * This module automatically enhances all file inputs in Active Admin forms
 * to use direct uploads to Azure Blob Storage instead of server-proxied uploads.
 * 
 * Features:
 * - Automatically detects and enhances file inputs
 * - Shows upload progress indicator
 * - Replaces file input with signed_id for form submission
 * - Works with both single and multiple file uploads
 */

import { DirectUpload } from '@rails/activestorage';
import {
  validateImageAspectRatio,
  validatePostPrimaryImageAspectRatio,
  IMAGE_ASPECT_RATIO_ERROR,
  POST_PRIMARY_IMAGE_ASPECT_RATIO_ERROR,
} from '@/lib/image-aspect-ratio-validation';

const DIRECT_UPLOAD_URL = '/rails/active_storage/direct_uploads';

/**
 * DirectUploadController manages the direct upload process for a file input
 */
class DirectUploadController {
  constructor(input) {
    this.input = input;
    this.form = input.closest('form');
    this.wrapper = null;
    this.progressBar = null;
    this.signedIdInput = null;
    this.originalName = input.name;
    
    this.setupUI();
    this.bindEvents();
  }

  setupUI() {
    // Create a wrapper around the file input
    this.wrapper = document.createElement('div');
    this.wrapper.className = 'direct-upload-wrapper';
    this.input.parentNode.insertBefore(this.wrapper, this.input);
    this.wrapper.appendChild(this.input);

    // Create progress bar
    this.progressBar = document.createElement('div');
    this.progressBar.className = 'direct-upload-progress';
    this.progressBar.innerHTML = `
      <div class="direct-upload-progress-bar">
        <div class="direct-upload-progress-fill"></div>
      </div>
      <span class="direct-upload-progress-text">Uploading...</span>
    `;
    this.progressBar.style.display = 'none';
    this.wrapper.appendChild(this.progressBar);

    // Create status indicator
    this.statusIndicator = document.createElement('div');
    this.statusIndicator.className = 'direct-upload-status';
    this.statusIndicator.style.display = 'none';
    this.wrapper.appendChild(this.statusIndicator);

    // Mark input as enhanced
    this.input.dataset.directUploadEnhanced = 'true';
  }

  bindEvents() {
    this.input.addEventListener('change', this.handleFileSelect.bind(this));
  }

  requiresPostPrimaryImageAspectRatioValidation() {
    if (this.input.dataset.validatePostPrimaryImageAspectRatio === 'true') {
      return true;
    }

    const name = this.input.name || '';
    return name.includes('new_display_media') && !name.includes('new_display_media_feed');
  }

  requiresImageAspectRatioValidation() {
    if (this.input.dataset.validateImageAspectRatio === 'true') {
      return true;
    }

    const name = this.input.name || '';
    return name.includes('new_display_media_feed');
  }

  async handleFileSelect(event) {
    const files = event.target.files;
    if (!files.length) return;

    // For simplicity, handle single file upload (Active Admin typically uses single files)
    const file = files[0];

    if (file.type.startsWith('image/')) {
      try {
        if (this.requiresPostPrimaryImageAspectRatioValidation()) {
          await validatePostPrimaryImageAspectRatio(file);
        } else if (this.requiresImageAspectRatioValidation()) {
          await validateImageAspectRatio(file);
        }
      } catch (error) {
        const message = error instanceof Error
          ? error.message
          : this.requiresPostPrimaryImageAspectRatioValidation()
            ? POST_PRIMARY_IMAGE_ASPECT_RATIO_ERROR
            : IMAGE_ASPECT_RATIO_ERROR;
        this.updateStatus('error', message);
        this.hideProgress();
        this.input.value = '';
        return;
      }
    }
    
    this.showProgress();
    this.updateStatus('preparing', 'Preparing upload...');

    try {
      const result = await this.uploadFile(file);
      
      // Create a hidden input with the signed_id
      this.createSignedIdInput(result.signed_id);
      
      // Update status to show success
      this.updateStatus('success', `✓ ${file.name} uploaded successfully`);
      this.hideProgress();
      
      // Disable the file input to prevent re-upload (the signed_id is what matters now)
      // But keep it visible so user can see what was selected
      
    } catch (error) {
      console.error('Direct upload failed:', error);
      this.updateStatus('error', `✗ Upload failed: ${error.message}`);
      this.hideProgress();
      
      // Clear the file input on error
      this.input.value = '';
      
      // Remove any existing signed_id input
      if (this.signedIdInput) {
        this.signedIdInput.remove();
        this.signedIdInput = null;
      }
    }
  }

  async uploadFile(file) {
    // Import chunked upload function dynamically
    const { uploadFileWithChunking } = await import('./azure_chunked_upload.js');
    
    try {
      // uploadFileWithChunking automatically handles chunking for files > 256MB
      const result = await uploadFileWithChunking(file, {
        onProgress: (progress) => {
          this.updateProgress(progress);
        }
      });

      return result;
    } catch (error) {
      console.error('[ActiveAdmin DirectUpload] Upload failed:', error);
      throw error;
    }
  }

  createSignedIdInput(signedId) {
    // Remove existing signed_id input if any
    if (this.signedIdInput) {
      this.signedIdInput.remove();
    }

    // Create hidden input with signed_id
    this.signedIdInput = document.createElement('input');
    this.signedIdInput.type = 'hidden';
    this.signedIdInput.name = this.originalName;
    this.signedIdInput.value = signedId;
    
    // Insert after the file input
    this.wrapper.appendChild(this.signedIdInput);
    
    // Change the file input name to prevent it from being submitted
    this.input.name = '';
  }

  showProgress() {
    this.progressBar.style.display = 'block';
    this.statusIndicator.style.display = 'none';
  }

  hideProgress() {
    this.progressBar.style.display = 'none';
  }

  updateProgress(percent) {
    const fill = this.progressBar.querySelector('.direct-upload-progress-fill');
    const text = this.progressBar.querySelector('.direct-upload-progress-text');
    
    fill.style.width = `${percent}%`;
    text.textContent = `Uploading... ${Math.round(percent)}%`;
  }

  updateStatus(type, message) {
    this.statusIndicator.style.display = 'block';
    this.statusIndicator.className = `direct-upload-status direct-upload-status--${type}`;
    this.statusIndicator.textContent = message;
  }
}

/**
 * Initialize direct upload enhancement for Active Admin
 */
function initActiveAdminDirectUploads() {
  // Find all file inputs in admin forms that haven't been enhanced
  const fileInputs = document.querySelectorAll(
    '.active_admin input[type="file"]:not([data-direct-upload-enhanced])'
  );

  fileInputs.forEach(input => {
    // Skip inputs that are part of the React Tiptap editor (handled separately)
    if (input.closest('.tiptap-toolbar') || input.closest('.react-tiptap-editor')) {
      return;
    }
    
    // Skip inputs that explicitly opt out
    if (input.dataset.skipDirectUpload === 'true') {
      return;
    }

    new DirectUploadController(input);
  });
}

/**
 * Add CSS styles for the direct upload UI
 */
function addDirectUploadStyles() {
  if (document.getElementById('direct-upload-styles')) return;

  const styles = document.createElement('style');
  styles.id = 'direct-upload-styles';
  styles.textContent = `
    .direct-upload-wrapper {
      position: relative;
    }

    .direct-upload-progress {
      margin-top: 8px;
      padding: 8px;
      background: #f5f5f5;
      border-radius: 4px;
    }

    .direct-upload-progress-bar {
      height: 8px;
      background: #e0e0e0;
      border-radius: 4px;
      overflow: hidden;
    }

    .direct-upload-progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #3b82f6, #60a5fa);
      border-radius: 4px;
      transition: width 0.2s ease;
      width: 0%;
    }

    .direct-upload-progress-text {
      display: block;
      margin-top: 4px;
      font-size: 12px;
      color: #666;
    }

    .direct-upload-status {
      margin-top: 8px;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 13px;
    }

    .direct-upload-status--preparing {
      background: #fef3cd;
      color: #856404;
    }

    .direct-upload-status--success {
      background: #d4edda;
      color: #155724;
    }

    .direct-upload-status--error {
      background: var(--ps-red-01, #fcd8d8);
      color: var(--ps-red, #f03e3e);
    }

    /* Animation for progress bar */
    .direct-upload-progress-fill {
      animation: pulse 1.5s ease-in-out infinite;
    }

    @keyframes pulse {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.7;
      }
    }
  `;
  document.head.appendChild(styles);
}

// Auto-initialize on DOM ready and Turbo page loads
function init() {
  addDirectUploadStyles();
  initActiveAdminDirectUploads();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Re-initialize on Turbo navigations
document.addEventListener('turbo:load', init);

// Re-initialize on dynamic content changes (for AJAX forms, etc.)
document.addEventListener('turbo:frame-load', init);

// Export for manual initialization if needed
export { initActiveAdminDirectUploads, DirectUploadController };

